import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiPut } from '../../lib/api';

/**
 * Seria de facturi a unei firme, dinspre client
 * ([#188](https://github.com/bplugaru/efx-pcrm/issues/188), fără probă din
 * [#201](https://github.com/bplugaru/efx-pcrm/issues/201)).
 *
 * Se completează din fișa firmei, dar **nu e un câmp al firmei**: stă în modulul de
 * facturare (`inv_series`), cu contorul ei, și se scrie pe ruta ei. De aceea nu e în
 * `companySchema` și nici în `CompanyInput` — salvarea fișei n-o atinge.
 *
 * Regulile — litere și cifre, startul blocat după primul număr, o serie veche reluată de unde a
 * rămas — sunt ale serverului. Ecranul le arată; nu le decide.
 */
const seriesSchema = z.object({
  code: z.string(),
  startNumber: z.number(),
  /** `null` = n-a ieșit niciun număr, deci startul se mai poate muta. */
  lastNumber: z.number().nullable(),
  /** Numărul următoarei facturi, scris ca pe factură: `0001`. Vine formatat de server. */
  nextNumber: z.string(),
  active: z.boolean(),
});

export type InvoiceSeries = z.infer<typeof seriesSchema>;

export interface InvoiceSeriesState {
  /** Seria activă, sau `null` dacă firma n-are încă una. */
  readonly series: InvoiceSeries | null;
  /** Toate seriile firmei: o serie veche reluată continuă de la numărul ei. */
  readonly all: readonly InvoiceSeries[];
}

export const invoiceSeriesQueryKey = (companyId: string) =>
  ['companies', companyId, 'invoice-series'] as const;

export const invoiceSeriesQueryOptions = (companyId: string) =>
  queryOptions({
    queryKey: invoiceSeriesQueryKey(companyId),
    queryFn: (): Promise<InvoiceSeriesState> =>
      apiGet(
        `/companies/${companyId}/invoice-series`,
        z.object({ series: seriesSchema.nullable(), all: z.array(seriesSchema) }),
      ),
  });

/** Seria activă: o creează, o reia sau îi mută startul. */
export function saveSeries(input: {
  companyId: string;
  code: string;
  startNumber: number;
}): Promise<InvoiceSeries> {
  return apiPut(
    `/companies/${input.companyId}/invoice-series`,
    { code: input.code, startNumber: input.startNumber },
    z.object({ series: seriesSchema }),
  ).then((body) => body.series);
}
